import type { ComponentProps, ReactElement } from "react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/utils";

/**
 * Shared link styling: keeps the primary link color at all times and only adds
 * an underline on hover. Never swaps the text color on hover.
 */
export const textLinkClassName = cn(
  "cursor-pointer text-link underline-offset-4 outline-none hover:underline focus-visible:underline dark:hover:text-link/90",
);

type TextLinkAsLinkProps = ComponentProps<typeof Link> & { to: ComponentProps<typeof Link>["to"] };
type TextLinkAsButtonProps = ComponentProps<"button"> & { to?: undefined };

type TextLinkProps = TextLinkAsLinkProps | TextLinkAsButtonProps;

/**
 * Renders a react-router `Link` when `to` is provided, otherwise a `button`
 * styled as a link (for actions that run logic before navigating).
 */
export function TextLink(props: TextLinkProps): ReactElement {
  if (props.to !== undefined) {
    const { className, ...rest } = props;
    return <Link className={cn(textLinkClassName, className)} {...rest} />;
  }

  const { className, type, ...rest } = props;
  return <button type={type ?? "button"} className={cn(textLinkClassName, className)} {...rest} />;
}
